/**
 * The boundary to @finixt/ui.
 *
 * App code imports finixt-ui only through this file (Feature 66, D7); ESLint
 * warns on any other import of the package. The re-exports are the components
 * the admin uses as finixt-ui draws them. Where the package's hand-written
 * types are weaker than this app's compiler settings, the type is narrowed
 * here: Table is generic over its row, and every optional prop admits an
 * explicit undefined, which exactOptionalPropertyTypes otherwise rejects at
 * each call site. Input is the one runtime correction; its comment says why.
 * Select is not re-exported: finixt-ui's is a native <select>, and the app's
 * own ui/Select opens the console's panel instead.
 *
 * A tag bump touches this file and the lockfile. Components that need
 * behavior the package lacks live beside this file, not in it.
 */
import { createElement, type ReactElement, type ReactNode } from 'react';

import {
  type BadgeProps as FinixtBadgeProps,
  EmptyState as FinixtEmptyState,
  type EmptyStateProps as FinixtEmptyStateProps,
  Input as FinixtInput,
  SegmentedControl as FinixtSegmentedControl,
  Table as FinixtTable,
  Textarea as FinixtTextarea,
  Toggle as FinixtToggle,
  type InputProps as FinixtInputProps,
  type SegmentedControlProps as FinixtSegmentedControlProps,
  type TableColumn as FinixtTableColumn,
  type TableProps as FinixtTableProps,
  type TextareaProps as FinixtTextareaProps,
  type ToggleProps as FinixtToggleProps,
} from '@finixt/ui';

export {
  AppLayout,
  Avatar,
  Badge,
  Banner,
  Button,
  Callout,
  Card,
  ContentLayout,
  CopyButton,
  DetailHeader,
  Divider,
  ErrorState,
  FilterChip,
  Grid,
  Icon,
  IconButton,
  IconTile,
  Inline,
  Kbd,
  KeyValue,
  Link,
  NavSection,
  OnboardingChecklist,
  Pagination,
  PanelHeader,
  PanelLayout,
  SearchField,
  SettingsLayout,
  ShellContext,
  Skeleton,
  Spinner,
  SplitLayout,
  Stack,
  StatCard,
  StatCell,
  TableToolbar,
  Tabs,
  Time,
  Toast,
  Typography,
  WizardLayout,
  relativeTime,
  usePersistedState,
} from '@finixt/ui';

export type {
  AppLayoutHandle,
  AppLayoutProps,
  ChecklistStep,
  KeyValueItem,
  BadgeProps,
  ButtonProps,
  ShellState,
  TypographyVariant,
} from '@finixt/ui';

/**
 * Every optional property widened to admit an explicit undefined. finixt-ui
 * declares `x?: T`; this app forwards possibly-undefined values and compiles
 * with exactOptionalPropertyTypes, which needs `x?: T | undefined`.
 */
type Loose<T> = {
  [K in keyof T as undefined extends T[K] ? K : never]?: T[K] | undefined;
} & {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
};

/** A column of a Table whose rows are `Row`. */
export type TableColumn<Row> = Loose<Omit<FinixtTableColumn, 'key' | 'render'>> & {
  /** A field of the row, or any key when `render` draws the cell. */
  key: Extract<keyof Row, string> | (string & {});
  render?: ((row: Row) => ReactNode) | undefined;
};

/** Table props generic over the row type. */
export type TableProps<Row> = Loose<
  Omit<FinixtTableProps, 'columns' | 'rows' | 'onRowClick' | 'rowKey'>
> & {
  columns: TableColumn<Row>[];
  rows: Row[];
  onRowClick?: ((row: Row) => void) | undefined;
  /** Field used as the React key. Default `id`. */
  rowKey?: Extract<keyof Row, string> | undefined;
};

/** finixt-ui's Table, typed over its row. The runtime is unchanged. */
export const Table = FinixtTable as unknown as <Row>(props: TableProps<Row>) => ReactElement;

/** The eight tones a Badge draws; the status vocabularies map onto them. */
export type BadgeTone = NonNullable<FinixtBadgeProps['tone']>;

/** finixt-ui's EmptyState, whose title may be a node; the runtime renders one. */
export type EmptyStateProps = Loose<Omit<FinixtEmptyStateProps, 'title'>> & { title: ReactNode };
export const EmptyState = FinixtEmptyState as unknown as (props: EmptyStateProps) => ReactElement;

/** The form controls, every optional prop admitting an explicit undefined. */
export type InputProps = Loose<FinixtInputProps>;
const BaseInput = FinixtInput as unknown as (
  props: InputProps & { className: string },
) => ReactElement;

/**
 * finixt-ui's Input with its right padding back. The package writes `padding:
 * var(--pad-input)` and then `paddingRight: undefined` when there is no kbd
 * hint; React turns that undefined into an empty right padding, so the
 * browser's own 0 to 2px shows and a date input's picker icon meets the
 * border. The `ui-input` rule in layer.css restores the right half.
 */
export function Input(props: InputProps): ReactElement {
  return createElement(BaseInput, { ...props, className: 'ui-input' });
}
export type TextareaProps = Loose<FinixtTextareaProps>;
export const Textarea = FinixtTextarea as unknown as (props: TextareaProps) => ReactElement;
export type ToggleProps = Loose<FinixtToggleProps>;
export const Toggle = FinixtToggle as unknown as (props: ToggleProps) => ReactElement;
export type SegmentedControlProps = Loose<FinixtSegmentedControlProps>;
export const SegmentedControl = FinixtSegmentedControl as unknown as (
  props: SegmentedControlProps,
) => ReactElement;
